import { InfiniteData } from '@tanstack/react-query';

import { FilterValues } from '@/hooks/use-filter-values';
import { PaginatedResponse } from '@/modules/common';
import { GroupBy } from '@/modules/common/types';

import { getAssignmentsByMember } from '../../api/assignments-by-member.api';
import {
  buildAssignmentFingerprint,
  buildStateChangeFingerprint,
  buildTimeOffFingerprint,
} from '../../helpers/assignment-cache-utils';
import {
  deepCopyMember,
  mergeAssignments,
  mergeStateChanges,
  mergeTimeOffs,
  mergeUtilizationByDate,
  PageResponseWithMetadata,
} from '../../helpers/planner-base/planner-pagination-helpers';
import { PlannerMemberResponse } from '../../types/dto';
import { useAssignmentsBase } from './use-assignments-base';

interface UseAssignmentsByMemberParams {
  filters: FilterValues['values'];
  enabled?: boolean;
}

/**
 * Build a content-level fingerprint for member pages.
 * Must change when mutations patch the cache so select() bypasses its memoization.
 */
function buildMemberFingerprint(
  data: InfiniteData<PageResponseWithMetadata<PlannerMemberResponse>>
): string {
  return data.pages
    .map(p => {
      const members = p.items as PlannerMemberResponse[];
      const assignmentFingerprint = members
        .flatMap(m => m.assignments.map(buildAssignmentFingerprint))
        .join(',');
      const timeOffFingerprint = members
        .flatMap(m => m.timeOffs.map(buildTimeOffFingerprint))
        .join(',');
      const utilizationFingerprint = members
        .map(m => m.utilizationByDate.reduce((sum, u) => sum + u.assigned, 0))
        .join(',');
      // Member-info column fields (location, department, capacity, rates, …) live on the
      // state changes, not on assignments — include them so editing them re-runs select().
      const stateChangeFingerprint = members
        .flatMap(m => m.stateChanges?.map(buildStateChangeFingerprint) ?? [])
        .join(',');
      return `${p.items.length}:${p.pagination?.cursor}:${p._dateRange.startDate}:${assignmentFingerprint}:${timeOffFingerprint}:${utilizationFingerprint}:${stateChangeFingerprint}`;
    })
    .join('|');
}

/**
 * Merges member data across all loaded pages (vertical + horizontal pagination).
 *
 * TODO: Currently merges ALL users from ALL loaded date ranges into a single list.
 * Users who only have data in a specific date range still appear when scrolling
 * to other periods. Ideally, visibility should be filtered by the current viewport.
 * Related: Same issue exists in use-assignments-by-department.ts and use-assignments-by-project.ts
 */
function selectMemberPages(
  data: InfiniteData<PageResponseWithMetadata<PlannerMemberResponse>>
): PaginatedResponse<PlannerMemberResponse> {
  // Combine all loaded pages to show the full visible date range
  const allItems = data.pages.flatMap(page => page.items);

  // Merge assignments for the same member across different date ranges and cursor pages
  const mergedMembersMap = new Map<number, PlannerMemberResponse>();

  allItems.forEach(plannerMemberResponse => {
    const existingMember = mergedMembersMap.get(plannerMemberResponse.user.id);

    if (existingMember) {
      // Merge assignments and their activePeriods across date ranges
      mergeAssignments(existingMember.assignments, plannerMemberResponse.assignments);

      // Merge time-off requests across date ranges
      mergeTimeOffs(existingMember.timeOffs, plannerMemberResponse.timeOffs);

      // Merge state changes across date ranges
      existingMember.stateChanges = mergeStateChanges(
        existingMember.stateChanges,
        plannerMemberResponse.stateChanges
      );

      // Merge utilization by date, avoiding duplicates
      existingMember.utilizationByDate = mergeUtilizationByDate(
        existingMember.utilizationByDate,
        plannerMemberResponse.utilizationByDate
      );
    } else {
      // Deep copy mutable arrays to prevent select() from mutating
      // TanStack Query's cached data across re-runs
      mergedMembersMap.set(plannerMemberResponse.user.id, deepCopyMember(plannerMemberResponse));
    }
  });

  // Get total from first page (represents total users, not affected by date range)
  const total = data.pages[0]?.pagination?.total ?? 0;

  // Don't sort — preserve insertion order from the backend so that new
  // vertical pages simply append rows at the bottom without reordering
  // existing rows (which causes visible UI jumps).
  return new PaginatedResponse<PlannerMemberResponse>({
    items: Array.from(mergedMembersMap.values()),
    pagination: {
      total,
      cursor: data.pages[data.pages.length - 1]?.pagination?.cursor ?? null,
    },
  });
}

/**
 * Hook for fetching planner assignments grouped by team member.
 * Supports dual-axis infinite scroll:
 * - Horizontal: Load past/future date ranges
 * - Vertical: Load more members via cursor pagination
 */
export function useAssignmentsByMember({ filters, enabled = true }: UseAssignmentsByMemberParams) {
  return useAssignmentsBase<PlannerMemberResponse>(
    { filters, enabled },
    {
      groupByKey: GroupBy.USERS,
      queryKeyPrefix: 'assignments-by-member',
      logTag: 'useAssignmentsByMember',
      apiFn: getAssignmentsByMember,
      selectFn: selectMemberPages,
      buildFingerprint: buildMemberFingerprint,
    }
  );
}
